/**
 * Phase Ads Stage 1 — DTO ↔ camelCase mappers
 * -----------------------------------------------------------------------------
 * snake_case wire format(`ads.dtos.ts`)→ camelCase domain(`ads.types.ts`)。
 *
 * R42.1 backend pivot:砍 OAuth start / Discovery 全套 mapper,加 OAuthLinkResult。
 */

import type {
  AccountCapabilityDto,
  AccountsListResponseDto,
  AdAccountDetailDto,
  AdsOAuthIdentityDto,
  AdAccountPatchRequestDto,
  AdAccountSummaryDto,
  BulkUnlinkRequestDto,
  BulkUnlinkResponseDto,
  BulkUnlinkedAccountRefDto,
  CancelSyncResponseDto,
  DiscoveredAccountDto,
  DiscoveryAccountsResultDto,
  LinkAccountsRequestDto,
  LinkAccountsResponseDto,
  LinkedAdAccountDto,
  OAuthIdentitiesResultDto,
  OAuthLinkRequestDto,
  OAuthLinkResultDto,
  GaUploadDto,
  AdAssetImportAcceptedDto,
  AdAccountAssetDto,
  AdAccountAssetsResponseDto,
  AdAssetImportJobDto,
  AdAssetImportRequestDto,
  GroupAeiReportDto,
  PostImportsRequestDto,
  PostImportsResponseDto,
  ReportQueryRequestDto,
  ReportQueryResponseDto,
  AssetGroupsQueryRequestDto,
  AssetGroupsResponseDto,
  AssetGroupThumbDto,
  AssetGroupMemberDto,
  AssetGroupMetricsDto,
  AssetGroupRowDto,
  SyncJobDetailDto,
  SyncJobDetailResponseDto,
  SyncJobsListResponseDto,
  SyncSubmitRequestDto,
  SyncSubmitResultDto,
  CreateMetaAssetUploadsResponseDto,
  MetaAssetUploadDto,
  MetaAssetUploadOutcomeDto,
  UnifiedAccountsResponseDto,
  UnifiedReportQueryRequestDto,
  MetaAssetThumbnailDto,
  AssetReportsQueryRequestDto,
  AssetReportsQueryResponseDto,
} from './ads.dtos';
import { isAdsPlatform } from './ads.types';
import type {
  AssetGroupsQuery,
  AssetGroupsResult,
  AssetGroupThumb,
  AssetGroupMember,
  AssetGroupMetrics,
  AssetGroupRow,
  AccountCapability,
  AdAccountDetail,
  AdAccountPatchRequest,
  AdAccountsOverview,
  AdAccountSummary,
  AdsOAuthIdentity,
  AdsPlatform,
  BulkUnlinkRequest,
  BulkUnlinkResponse,
  BulkUnlinkedAccountRef,
  CancelSyncResponse,
  CreateAssetImportsRequest,
  CreateMetaAssetUploadsResponse,
  MetaAssetUpload,
  MetaAssetUploadOutcome,
  CreateAssetImportsResponse,
  DiscoveredAccount,
  DiscoveryAccountsResult,
  GaUpload,
  AdAssetImportAccepted,
  AdAssetImportItemResult,
  AdAccountAsset,
  AdAccountAssetsResult,
  AdAssetImportJob,
  AdAssetImportRequest,
  AdAssetImportStatus,
  GroupAeiReport,
  LinkAccountsRequest,
  LinkAccountsResponse,
  LinkedAdAccount,
  OAuthLinkRequest,
  OAuthLinkResult,
  ReportQueryRequest,
  ReportQueryResponse,
  SyncJobDetail,
  SyncJobsListResponse,
  SyncSubmitRequest,
  SyncSubmitResult,
  UnifiedReportQueryRequest,
  MetaAssetThumbnail,
  AssetReportsQueryRequest,
  AssetReportsQueryResponse,
} from './ads.types';

// ===== Common =====

export function mapDiscoveredAccountDto(dto: DiscoveredAccountDto): DiscoveredAccount {
  return {
    customerId: dto.customer_id,
    descriptiveName: dto.descriptive_name,
    isManager: dto.is_manager,
    currencyCode: dto.currency_code,
    timeZone: dto.time_zone,
    level: dto.level,
    parentCustomerId: dto.parent_customer_id,
    testAccount: dto.test_account,
    autoTaggingEnabled: dto.auto_tagging_enabled,
    status: dto.status,
    // R299 — Meta 原生權限;缺欄 / 非陣列一律 null(= 未知,不猜「唯讀」也不猜「可寫」)。
    userTasks: stringArrayOrNull(dto.user_tasks),
  };
}

/** wire `list[str] | None` → `string[] | null`(非字串元素丟掉;缺欄 / 非陣列 → null)。 */
function stringArrayOrNull(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null;
  return v.filter((x): x is string => typeof x === 'string');
}

/** wire `bool | None` → `boolean | null`(缺欄 / 非布林 → null = 未知)。 */
function booleanOrNull(v: unknown): boolean | null {
  return typeof v === 'boolean' ? v : null;
}

/** wire 顯示字串:缺欄 / 空字串 → null(「不知道」只有一種寫法)。 */
function nonEmptyStringOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

// ===== OAuth 授權身分(R290 多 Google 身分綁定;R299 Meta 同形)=====

export function mapAdsOAuthIdentityDto(dto: AdsOAuthIdentityDto): AdsOAuthIdentity {
  return {
    oauthUserId: dto.oauth_user_id,
    // email 是顯示欄;wire 缺值/非字串一律收斂成空字串,呈現端只有一條退路要處理。
    oauthEmail: typeof dto.oauth_email === 'string' ? dto.oauth_email : '',
    // R299 — Meta 才有;Google 端點缺欄 → 空字串(呈現端同一條退路)。
    oauthName: typeof dto.oauth_name === 'string' ? dto.oauth_name : '',
    status: dto.status,
    // R299 — Meta 長期 token 到期;Google 缺欄 → null(= 不會到期,不畫提示)。
    expiresAt: nonEmptyStringOrNull(dto.expires_at),
    createdAt: dto.created_at ?? null,
  };
}

export function mapOAuthIdentitiesResultDto(
  dto: OAuthIdentitiesResultDto,
): AdsOAuthIdentity[] {
  return (dto.identities ?? []).map(mapAdsOAuthIdentityDto);
}

/**
 * `POST /oauth/link` body(R290)。
 *
 * 沒指定身分 → 回 `undefined` = **request 不帶 body**,與 R288 之前逐位元相同
 * (BE 那側「1 把身分且未指定 → 用它」的相容路徑);指定了才送單鍵 body。
 */
export function oauthLinkRequestToDto(
  req?: OAuthLinkRequest,
): OAuthLinkRequestDto | undefined {
  const id = req?.oauthUserId;
  return id ? { oauth_user_id: id } : undefined;
}

// ===== OAuth Link(R42.1 主 mapper;R237 規模化三欄;R290 三 state)=====

export function mapOAuthLinkResultDto(dto: OAuthLinkResultDto): OAuthLinkResult {
  if (dto.state === 'DISCOVERED') {
    return {
      state: 'DISCOVERED',
      discoveryId: dto.discovery_id,
      discoveredAccounts: dto.discovered_accounts.map(mapDiscoveredAccountDto),
      discoveredTotal: dto.discovered_total,
      hasMore: dto.has_more,
      // R290(docs/105 §3.2)— wire 上這兩欄是 required `str`,但 BE model 預設 `""`
      // = 不知道;部署前則整欄不存在。缺欄 / "" 一律收斂 null,**不猜**成「當前身分」
      // (與 accounts 的 oauth_email 同語意)。
      oauthUserId:
        typeof dto.oauth_user_id === 'string' && dto.oauth_user_id.length > 0
          ? dto.oauth_user_id
          : null,
      oauthEmail:
        typeof dto.oauth_email === 'string' && dto.oauth_email.length > 0
          ? dto.oauth_email
          : null,
      // R300 — 舊 build 缺欄 → [](挑選頁退回自己撈 GET /accounts);非字串元素丟掉
      boundCustomerIds: stringArrayOrNull(dto.bound_customer_ids) ?? [],
    };
  }
  if (dto.state === 'IDENTITY_REQUIRED') {
    return {
      state: 'IDENTITY_REQUIRED',
      identities: (dto.identities ?? []).map(mapAdsOAuthIdentityDto),
    };
  }
  if (dto.state === 'NEEDS_AUTHORIZATION') {
    return {
      state: 'NEEDS_AUTHORIZATION',
      authorizationUrl: dto.authorization_url,
      requiredScopes: dto.required_scopes,
      reason: dto.reason,
    };
  }
  // 值域外的 state = BE 開了新流程而 FE 還不會走。這裡**刻意丟錯**而不是硬塞成
  // NEEDS_AUTHORIZATION:後者會做出一個 `authorizationUrl: undefined` 的物件,
  // 錯誤在幾層之後才以「按了沒反應」現形(R275 那族症狀)。丟錯 → caller 的
  // catch 走既有 toast 路徑,console 有 state 原值可查。
  throw new Error(
    `[ads] unsupported oauth/link state: ${String((dto as { state?: unknown }).state)}`,
  );
}

// ===== Discovery accounts 分頁/搜尋(R237)=====

export function mapDiscoveryAccountsResultDto(
  dto: DiscoveryAccountsResultDto,
): DiscoveryAccountsResult {
  return {
    discoveryId: dto.discovery_id,
    total: dto.total,
    filteredTotal: dto.filtered_total,
    offset: dto.offset,
    limit: dto.limit,
    hasMore: dto.has_more,
    accounts: dto.accounts.map(mapDiscoveredAccountDto),
    // 舊 build / 非展開請求 → undefined|null 一律收斂成 null
    expandStatus: typeof dto.expand_status === 'string' ? dto.expand_status : null,
  };
}

// ===== Link Accounts(R42.12 取代 Finalize;POST /accounts)=====

export function linkAccountsRequestToDto(req: LinkAccountsRequest): LinkAccountsRequestDto {
  return {
    discovery_id: req.discoveryId,
    selected_customer_ids: req.selectedCustomerIds,
  };
}

export function mapLinkedAdAccountDto(dto: LinkedAdAccountDto): LinkedAdAccount {
  return {
    adAccountId: dto.ad_account_id,
    customerId: dto.customer_id,
    descriptiveName: dto.descriptive_name,
    isManager: dto.is_manager,
    alreadyExisted: dto.already_existed,
    // R299 — Meta finalize 帶 write_ok;Google 缺欄 → null
    writeOk: booleanOrNull(dto.write_ok),
    // R300 — 改綁旗標;缺欄 → false(不猜)
    rebound: dto.rebound === true,
  };
}

export function mapLinkAccountsResponseDto(
  dto: LinkAccountsResponseDto,
): LinkAccountsResponse {
  return { adAccounts: dto.ad_accounts.map(mapLinkedAdAccountDto) };
}

// ===== Bulk Unlink(R42.12 新加;DELETE /accounts 批次解除)=====

export function bulkUnlinkRequestToDto(req: BulkUnlinkRequest): BulkUnlinkRequestDto {
  return { deleted_customer_ids: req.deletedCustomerIds };
}

export function mapBulkUnlinkedAccountRefDto(
  dto: BulkUnlinkedAccountRefDto,
): BulkUnlinkedAccountRef {
  return {
    adAccountId: dto.ad_account_id,
    customerId: dto.customer_id,
  };
}

export function mapBulkUnlinkResponseDto(dto: BulkUnlinkResponseDto): BulkUnlinkResponse {
  return {
    deleted: dto.deleted.map(mapBulkUnlinkedAccountRefDto),
    skippedCustomerIds: dto.skipped_customer_ids,
  };
}

// ===== Ad accounts(R42.12 對齊 2026-05-18 BE spec §4.1)=====

/**
 * @param fallbackPlatform R299 — wire `platform` 缺欄(BE 2026-08-31 之前的 build)時
 *   以**呼叫的端點**補:單平台端點知道自己是誰(`/ads/google/accounts` 一定是 google),
 *   總覽端點的列一定帶欄(required)。⚠️ 不可以預設 'google' 一路吃到底 —— 那會讓
 *   舊 build 上的 Meta 列被畫成 Google 帳戶。所以這個參數是 **required**。
 *   wire 帶了認不得的值 → 一樣退回 fallback(呈現層拿不到第三個平台的圖示與規則;
 *   總覽 mapper 對這種列另有處置,見 {@link mapUnifiedAccountsResponseDto})。
 */
export function mapAdAccountSummaryDto(
  dto: AdAccountSummaryDto,
  fallbackPlatform: AdsPlatform,
): AdAccountSummary {
  return {
    platform: isAdsPlatform(dto.platform) ? dto.platform : fallbackPlatform,
    adAccountId: dto.ad_account_id,
    customerId: dto.customer_id,
    descriptiveName: dto.descriptive_name,
    isManager: dto.is_manager,
    currencyCode: dto.currency_code,
    timeZone: dto.time_zone,
    syncEnabled: dto.sync_enabled,
    syncStatus: dto.sync_status,
    lastSyncAt: dto.last_sync_at,
    lastErrorClass: dto.last_error_class,
    status: dto.status,
    // 總覽列沒有這欄(`UnifiedAdAccountOut`)→ null
    discoveredViaAnchorId: dto.discovered_via_anchor_id ?? null,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    // R290 — 綁定來源。缺欄(BE 未部署)與空字串(存量未回填 / Meta 沒 email)同樣是
    // 「不知道」→ null。
    oauthEmail: nonEmptyStringOrNull(dto.oauth_email),
    // R301.1 — legacy 未回填是 `""`,與「缺欄」同義 → null(呈現端退連結頁,不猜)
    oauthUserId: nonEmptyStringOrNull(dto.oauth_user_id),
    // R299 — Meta 三欄;Google 列 / 舊 build 缺欄 → null(= 未知,不猜)
    userTasks: stringArrayOrNull(dto.user_tasks),
    writeOk: booleanOrNull(dto.write_ok),
    // R308 — 缺欄 / 空字串一律 null(= 從沒探過);**不猜**成「剛探過」
    capabilityProbedAt:
      typeof dto.capability_probed_at === 'string' && dto.capability_probed_at.length > 0
        ? dto.capability_probed_at
        : null,
    accessRole:
      typeof dto.access_role === 'string' && dto.access_role.length > 0 ? dto.access_role : null,
    accountStatus: nonEmptyStringOrNull(dto.account_status),
  };
}

export function mapAccountsListResponseDto(
  dto: AccountsListResponseDto,
  platform: AdsPlatform,
): AdAccountSummary[] {
  return dto.accounts.map((a) => mapAdAccountSummaryDto(a, platform));
}

/**
 * R299 — `GET /api/ads/accounts` 總覽(docs/109 §6)。
 *
 * - `platforms`:只留認得的值(BE Literal `google` / `meta`);認不得的值代表 BE 開了
 *   第三個平台而 FE 還沒有它的圖示 / 錯誤 slug / 授權起點 —— 濾掉並 `console.warn`,
 *   讓「Meta 分頁顯不顯」這類判斷不會拿到一個畫不出來的平台。
 * - `accounts`:列的 `platform` 是 required;認不得的列同樣濾掉(它的寫操作沒有端點
 *   可走,畫成 Google 帳戶更糟)。⚠️ 這**不是** R228 那種「用 FE union 過濾已 ship 的
 *   wire 值域」—— 已 ship 的兩個值都認得;新平台本來就要 FE 加一輪才畫得出來。
 */
export function mapUnifiedAccountsResponseDto(dto: UnifiedAccountsResponseDto): AdAccountsOverview {
  const platforms: AdsPlatform[] = [];
  for (const p of dto.platforms ?? []) {
    if (isAdsPlatform(p)) platforms.push(p);
    else console.warn('[ads] unknown platform in GET /ads/accounts platforms:', p);
  }
  const accounts: AdAccountSummary[] = [];
  for (const row of dto.accounts ?? []) {
    if (!isAdsPlatform(row.platform)) {
      console.warn('[ads] unknown platform on account row, skipped:', row.platform, row.ad_account_id);
      continue;
    }
    accounts.push(mapAdAccountSummaryDto(row, row.platform));
  }
  return { platforms, accounts };
}

/** R42.12 — detail = summary 同 mapper(BE spec §4.2 detail = summary shape)。 */
export function mapAdAccountDetailDto(
  dto: AdAccountDetailDto,
  platform: AdsPlatform,
): AdAccountDetail {
  return {
    ...mapAdAccountSummaryDto(dto, platform),
  };
}

export function accountPatchRequestToDto(
  req: AdAccountPatchRequest,
): AdAccountPatchRequestDto {
  return { sync_enabled: req.syncEnabled };
}

/** R42.12 — `GET / PATCH /accounts/{aid}` envelope unwrap helper(BE spec §4.2)。 */
export function mapAccountDetailResponseDto(
  dto: { ad_account: AdAccountDetailDto },
  platform: AdsPlatform,
): AdAccountDetail {
  return mapAdAccountDetailDto(dto.ad_account, platform);
}

// ===== Sync jobs =====

export function syncSubmitRequestToDto(req: SyncSubmitRequest): SyncSubmitRequestDto {
  const out: SyncSubmitRequestDto = { ad_account_ids: req.adAccountIds };
  if (req.mode !== undefined) out.mode = req.mode;
  if (req.dateRange !== undefined) out.date_range = req.dateRange;
  return out;
}

export function mapSyncSubmitResultDto(dto: SyncSubmitResultDto): SyncSubmitResult {
  return {
    syncJobs: dto.sync_jobs.map((j) => ({
      syncJobId: j.sync_job_id,
      adAccountId: j.ad_account_id,
      customerId: j.customer_id,
      totalSubtasks: j.total_subtasks,
    })),
  };
}

/**
 * `SyncJobDetailDto` 的鍵名 runtime 清單 —— 給契約鎖比對用(TS 型別在 runtime 不存在)。
 *
 * 對齊 `REPORT_DTO_KEYS` 慣例(check.mappers.ts):下面 `_syncKeysExhaustive` 讓
 * 「DTO 加了欄位但忘了加進這裡」在 **tsc** 就紅;[beContract.test](@/contract/beContract.test.ts)
 * 拿它與 BE `SyncJobOut.model_fields` **雙向**對比 —— R227 那隻(FE 讀 `total_subtasks`
 * 而 BE ship `subtasks_total`,分母 undefined → 進度條凍 0%)在兩個方向各紅一條。
 */
export const SYNC_JOB_DETAIL_DTO_KEYS = [
  'sync_job_id', 'ad_account_id', 'customer_id', 'mode', 'date_range',
  'status', 'phase',
  'subtasks_total', 'subtasks_done', 'subtasks_failed', 'subtasks_split',
  'progress_hint', 'error_class', 'trace_id',
  'created_at', 'updated_at', 'started_at', 'finished_at',
] as const;

/** DTO 加欄但沒補進 `SYNC_JOB_DETAIL_DTO_KEYS` → 這一行 tsc 紅。 */
type _MissingSyncDtoKey = Exclude<keyof SyncJobDetailDto, (typeof SYNC_JOB_DETAIL_DTO_KEYS)[number]>;
const _syncKeysExhaustive: _MissingSyncDtoKey extends never ? true : never = true;
void _syncKeysExhaustive;

/**
 * 對齊 docs/36 §2 真實 wire format(R44.1;R227 訂正 `subtasks_total`)。
 *
 * ⚠️ detail/list 的分母欄名是 `subtasks_total`,**與 submit 回應的 `total_subtasks`
 * 不對稱**(兩邊都是 BE wire 事實,見 `SyncJobDetailDto` 註解)。R227 前這裡讀
 * `dto.total_subtasks` → detail/list 永遠 undefined → `syncJobProgressPercent` 恆 0。
 */
export function mapSyncJobDetailDto(dto: SyncJobDetailDto): SyncJobDetail {
  return {
    syncJobId: dto.sync_job_id,
    adAccountId: dto.ad_account_id,
    customerId: dto.customer_id,
    mode: dto.mode,
    dateRange: dto.date_range,

    status: dto.status,
    phase: dto.phase,

    totalSubtasks: dto.subtasks_total,
    subtasksDone: dto.subtasks_done,
    subtasksFailed: dto.subtasks_failed,
    subtasksSplit: dto.subtasks_split,

    progressHint: dto.progress_hint,
    errorClass: dto.error_class,
    traceId: dto.trace_id,

    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    startedAt: dto.started_at,
    finishedAt: dto.finished_at,
  };
}

/** `GET /sync/jobs/{jid}` envelope unwrap(docs/36 §3)。 */
export function mapSyncJobDetailResponseDto(
  dto: SyncJobDetailResponseDto,
): SyncJobDetail {
  return mapSyncJobDetailDto(dto.sync_job);
}

export function mapSyncJobsListResponseDto(
  dto: SyncJobsListResponseDto,
): SyncJobsListResponse {
  return {
    syncJobs: dto.sync_jobs.map(mapSyncJobDetailDto),
    nextCursor: dto.next_cursor,
  };
}

/** R44.1 — cancel BE 真實回 `{ sync_job_id, status }`(docs/36 §4)。 */
export function mapCancelSyncResponseDto(dto: CancelSyncResponseDto): CancelSyncResponse {
  return {
    syncJobId: dto.sync_job_id,
    status: dto.status,
  };
}

/**
 * 派生 sync_jobs/{jid} 主 doc 的 progress percent。
 * 對齊 docs/31 §4.2 FE 自算公式:FETCH 80% / DEDUPE 15% / DONE 收尾 5%。
 */
export function syncJobProgressPercent(
  detail: Pick<SyncJobDetail, 'phase' | 'subtasksDone' | 'totalSubtasks'>,
): number {
  const ratio = detail.totalSubtasks > 0 ? detail.subtasksDone / detail.totalSubtasks : 0;
  const fetchPct = Math.min(80, ratio * 80);
  const dedupePct = detail.phase === 'DEDUPE' || detail.phase === 'DONE' ? 15 : 0;
  const donePct = detail.phase === 'DONE' ? 5 : 0;
  return Math.min(100, Math.round(fetchPct + dedupePct + donePct));
}

// ===== Reports =====

/**
 * R342 — Meta 縮圖換發結果。`source` 照 wire 讀(開放字串),不收斂成布林 ——
 * 呈現端要分得出「我方 signed URL(恆可載入)」與「Meta CDN 原址(隨時會腐)」。
 */
export function mapMetaAssetThumbnailDto(dto: MetaAssetThumbnailDto): MetaAssetThumbnail {
  return {
    assetId: dto.asset_id,
    url: dto.url,
    expiresAt: dto.expires_at ?? null,
    source: dto.source,
  };
}

export function reportQueryRequestToDto(req: ReportQueryRequest): ReportQueryRequestDto {
  const out: ReportQueryRequestDto = {
    metrics: req.metrics,
    group_by: req.groupBy,
    date_range: req.dateRange,
  };
  if (req.filters) {
    out.filters = {};
    // R326 — 統一面專屬(docs/113):單平台面帶這欄 = 422,呼叫端責任見型別註解。
    if (req.filters.platforms?.length) out.filters.platforms = req.filters.platforms;
    if (req.filters.adAccountIds) out.filters.ad_account_ids = req.filters.adAccountIds;
    if (req.filters.customerIds) out.filters.customer_ids = req.filters.customerIds;
    if (req.filters.campaignIds) out.filters.campaign_ids = req.filters.campaignIds;
    if (req.filters.segmentDevice) out.filters.segment_device = req.filters.segmentDevice;
    if (req.filters.ourAssetId) out.filters.our_asset_id = req.filters.ourAssetId;
    // 素材成效(docs/38 §2b)— `asset_link_scope` 出現即為素材成效查詢,BE 據此路由
    // `google_asset_metrics_clean` 並鎖單一 entity_kind;非素材查詢**絕不可帶**(422)。
    if (req.filters.assetIds) out.filters.asset_ids = req.filters.assetIds;
    // 掛載層兩把 filter **互斥**(BE `reports_models.py:242` 明文 422)。
    // chips(複選)是新模型 → 有它就以它為準,舊單值一律不送;真的兩個都給了是
    // 呼叫端的 bug,但整支 422 = 整張表空白,所以這裡收斂成「以新模型為準 + 出聲」。
    if (req.filters.assetLinkScopes?.length) {
      out.filters.asset_link_scopes = req.filters.assetLinkScopes;
      if (req.filters.assetLinkScope) {
        // developer-only 訊息 → 英文(`hai/no-hardcoded-cjk` 的慣例)
        console.warn(
          '[reports] filters.assetLinkScope and assetLinkScopes are mutually exclusive (BE 422); sending the multi-select one only',
        );
      }
    } else if (req.filters.assetLinkScope) {
      out.filters.asset_link_scope = req.filters.assetLinkScope;
    }
    if (req.filters.similarityLevel) out.filters.similarity_level = req.filters.similarityLevel;
    // R241 —— 空字串是 422,所以只在非空時帶(呼叫端已 trim;這裡是第二道)
    if (req.filters.assetNameQuery) out.filters.asset_name_query = req.filters.assetNameQuery;
    // R246 —— 媒體類型與「只看有投放」改後端篩(前端過濾在分頁下必然出錯)。
    // 空陣列 = 沒選 = 全部 → 不帶(帶空陣列語意不明);`false` 是 BE 預設 → 不帶。
    if (req.filters.assetMediaTypes?.length) {
      out.filters.asset_media_types = req.filters.assetMediaTypes;
    }
    if (req.filters.onlyWithDelivery) out.filters.only_with_delivery = true;
    // docs/93 §A7 —— breakdown 與「每層一列」/「舊單值鎖層」**三者互斥**(BE 422)。
    // 送錯整支 422 = 整張表「讀取失敗」,所以這裡也收斂成「以新模型為準 + 出聲」,
    // 與上面那組 chips 的處置同一套判準。
    if (req.filters.includeScopeBreakdown) {
      const clash =
        req.groupBy.includes('asset_link_scope') || req.filters.assetLinkScope !== undefined;
      if (clash) {
        console.warn(
          '[reports] include_scope_breakdown is mutually exclusive with group_by asset_link_scope / filters.assetLinkScope (BE 422); dropping the legacy per-layer form',
        );
        out.group_by = out.group_by.filter((g) => g !== 'asset_link_scope');
        delete out.filters.asset_link_scope;
      }
      out.filters.include_scope_breakdown = true;
    }
  }
  if (req.orderBy) out.order_by = req.orderBy;
  if (req.limit !== undefined) out.limit = req.limit;
  if (req.cursor !== undefined) out.cursor = req.cursor;
  return out;
}

/** R302 — 統一成效查詢 request → wire(docs/113 §1)。 */
export function unifiedReportQueryRequestToDto(
  req: UnifiedReportQueryRequest,
): UnifiedReportQueryRequestDto {
  const out: UnifiedReportQueryRequestDto = {
    metrics: req.metrics,
    group_by: req.groupBy,
    date_range: req.dateRange,
  };
  if (req.filters) {
    const f: NonNullable<UnifiedReportQueryRequestDto['filters']> = {};
    if (req.filters.platforms?.length) f.platforms = req.filters.platforms;
    if (req.filters.adAccountIds?.length) f.ad_account_ids = req.filters.adAccountIds;
    if (req.filters.customerIds?.length) f.customer_ids = req.filters.customerIds;
    if (req.filters.campaignIds?.length) f.campaign_ids = req.filters.campaignIds;
    if (Object.keys(f).length > 0) out.filters = f;
  }
  if (req.orderBy) out.order_by = req.orderBy;
  if (req.limit !== undefined) out.limit = req.limit;
  if (req.cursor !== undefined) out.cursor = req.cursor;
  return out;
}

/**
 * R410 — 素材成效跨媒體合併報表 request → wire(BE docs/135 §1)。
 *
 * ⚠️ 空陣列一律不帶(`platforms: []` / `kinds: []` 的語意是「全部」,但不帶才是 BE 文件寫的
 * 「全部」;帶空陣列等於讓 cursor sig 多一個與語意無關的差異)。空字串搜尋 BE 422 → 不帶。
 * `only_with_delivery` 只在 true 時帶(false 是 BE 預設)。
 */
export function assetReportsQueryRequestToDto(
  req: AssetReportsQueryRequest,
): AssetReportsQueryRequestDto {
  const out: AssetReportsQueryRequestDto = {
    metrics: req.metrics,
    group_by: req.groupBy,
    date_range: req.dateRange,
  };
  if (req.filters) {
    const src = req.filters;
    const f: NonNullable<AssetReportsQueryRequestDto['filters']> = {};
    if (src.platforms?.length) f.platforms = src.platforms;
    if (src.customerIds?.length) f.customer_ids = src.customerIds;
    if (src.adAccountIds?.length) f.ad_account_ids = src.adAccountIds;
    if (src.kinds?.length) f.kinds = src.kinds;
    if (src.placementTypes?.length) f.placement_types = src.placementTypes;
    if (src.similarityLevel) f.similarity_level = src.similarityLevel;
    if (src.assetNameQuery) f.asset_name_query = src.assetNameQuery;
    if (src.assetRefs?.length) {
      f.asset_refs = src.assetRefs.map((r) => ({ platform: r.platform, asset_id: r.assetId }));
    }
    // R428 — 組鍵值原樣送(不 trim、不解析;名稱鍵的頭尾空白 BE 已去過,值就是列上那一份)
    if (src.familyKey) f.family_key = { key: src.familyKey.key, value: src.familyKey.value };
    if (src.onlyWithDelivery) f.only_with_delivery = true;
    if (Object.keys(f).length > 0) out.filters = f;
  }
  if (req.orderBy) out.order_by = req.orderBy;
  if (req.limit !== undefined) out.limit = req.limit;
  if (req.cursor !== undefined) out.cursor = req.cursor;
  return out;
}

/**
 * R410 — 素材成效跨媒體合併報表 response(同既有 envelope + 快查表兩欄)。
 *
 * `mart_ready` 缺欄 → `true`(舊 build / fixture 不該被讀成「表還沒建好」);只有明確的
 * `false` 才是準備中。`data_as_of` 空頁時 BE 回 null → 原樣保留 null(呼叫端沿用前一頁)。
 */
export function mapAssetReportsQueryResponseDto(
  dto: AssetReportsQueryResponseDto,
): AssetReportsQueryResponse {
  const byPlatform = mapDataAsOfByPlatform(dto.data_as_of_by_platform);
  if (byPlatform) {
    for (const p of Object.keys(byPlatform) as AdsPlatform[]) {
      const v = byPlatform[p];
      byPlatform[p] = typeof v === 'string' ? withWireOffset(v, TAIPEI_OFFSET) : (v ?? null);
    }
  }
  return {
    rows: Array.isArray(dto.rows) ? dto.rows : [],
    schema: dto.schema,
    dataAsOf: typeof dto.data_as_of === 'string' && dto.data_as_of ? withWireOffset(dto.data_as_of, TAIPEI_OFFSET) : null,
    cacheHit: dto.cache_hit,
    bqJobId: dto.bq_job_id,
    bytesBilled: dto.bytes_billed,
    nextCursor: dto.next_cursor ?? null,
    totalRows: dto.total_rows ?? null,
    aggregationCaveat: dto.aggregation_caveat ?? null,
    dataAsOfByPlatform: byPlatform,
    martBuiltAt:
      typeof dto.mart_built_at === 'string' && dto.mart_built_at ? withWireOffset(dto.mart_built_at, 'Z') : null,
    martReady: dto.mart_ready !== false,
  };
}

/** 台灣時區(無夏令時間;BE 的同步水位是 Asia/Taipei 的 DATETIME,不帶時區)。 */
const TAIPEI_OFFSET = '+08:00';

/**
 * R410 — 把「不帶時區」的 wire 時間補上**它實際所在的時區**,之後 `new Date()` 才會解對。
 *
 * ⚠️ JS 對 `2026-10-10T11:00:00`(有時分、無時區)一律當**瀏覽器當地時間** —— 人在台灣看剛好對,
 * 換個時區就差好幾小時,而且畫面完全正常(就是一個時間)。docs/135:`data_as_of` 是**台北時間不帶時區**、
 * `mart_built_at` 是 **UTC**。已帶 `Z` / `±hh:mm` 的值原樣保留(`data_as_of` 沒有水位時退回 `mart_built_at`,
 * 那時就帶著 UTC 的 offset)。純日期(`YYYY-MM-DD`)不動。
 */
export function withWireOffset(v: string, offset: string): string {
  const s = v.trim();
  if (!/T\d{2}:\d{2}/.test(s)) return s;
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(s)) return s;
  return `${s}${offset}`;
}

/**
 * R308 — 重探結果。三個欄位**各自獨立收斂**,`probed_at` 缺 → null(= 沒探到,呈現端不可當唯讀)。
 */
export function mapAccountCapabilityDto(dto: AccountCapabilityDto): AccountCapability {
  return {
    writeOk: booleanOrNull(dto.write_ok),
    accessRole: typeof dto.access_role === 'string' && dto.access_role.length > 0 ? dto.access_role : null,
    probedAt: typeof dto.probed_at === 'string' && dto.probed_at.length > 0 ? dto.probed_at : null,
  };
}

export function mapReportQueryResponseDto(
  dto: ReportQueryResponseDto,
): ReportQueryResponse {
  return {
    rows: dto.rows,
    schema: dto.schema,
    dataAsOf: dto.data_as_of,
    cacheHit: dto.cache_hit,
    bqJobId: dto.bq_job_id,
    bytesBilled: dto.bytes_billed,
    nextCursor: dto.next_cursor,
    // 舊 build 沒這欄 → null;顯示端據此退回「已載入 N 列」措辭,不假裝知道總數
    totalRows: dto.total_rows ?? null,
    // 舊 build 沒這欄 → null(= 非素材查詢的語意,fail-soft)
    aggregationCaveat: dto.aggregation_caveat ?? null,
    dataAsOfByPlatform: mapDataAsOfByPlatform(dto.data_as_of_by_platform),
  };
}

/**
 * R302.1 — `data_as_of_by_platform` 只留 FE 認得的平台鍵(第三個平台的時間沒有圖示 / 文案可畫,
 * 同 R299 `mapUnifiedAccountsResponseDto` 的處理);`null` 值保留(= 該平台沒有 watermark,不是缺欄);
 * 缺欄 / null(Google 面、舊 build)→ `null`,不補空物件。
 */
function mapDataAsOfByPlatform(
  v: Record<string, string | null> | null | undefined,
): Partial<Record<AdsPlatform, string | null>> | null {
  if (!v || typeof v !== 'object') return null;
  const out: Partial<Record<AdsPlatform, string | null>> = {};
  for (const [k, t] of Object.entries(v)) {
    if (isAdsPlatform(k)) out[k] = typeof t === 'string' ? t : null;
  }
  return out;
}

// ===== I1(docs/89 §3b)— 廣告帳戶素材 → 我們的素材庫 =====

export function adAssetImportRequestToDto(
  req: AdAssetImportRequest,
): AdAssetImportRequestDto {
  return {
    customer_id: req.customerId,
    asset_ids: req.assetIds,
    library_id: req.libraryId,
  };
}

export function mapAdAssetImportAcceptedDto(
  dto: AdAssetImportAcceptedDto,
): AdAssetImportAccepted {
  return {
    jobId: dto.job_id,
    assetCount: dto.asset_count ?? 0,
    status: (dto.status as AdAssetImportStatus) ?? 'PENDING',
  };
}

export function mapAdAssetImportJobDto(dto: AdAssetImportJobDto): AdAssetImportJob {
  const results: Record<string, AdAssetImportItemResult> = {};
  for (const [assetId, r] of Object.entries(dto.results ?? {})) {
    results[assetId] = {
      // per-asset status 原樣帶(值域 `DONE`/`FAILED`/`SKIPPED_UNSUPPORTED`;
      // BE 若加新值,呼叫端的 outcome 拆解會歸進 failed 那側 —— 保守方向)
      status: r.status,
      itemId: r.item_id ?? null,
      error: r.error ?? null,
      duplicateOfItemId: r.duplicate_of_item_id ?? null,
    };
  }
  return {
    jobId: dto.job_id,
    status: (dto.status as AdAssetImportStatus) ?? 'PENDING',
    total: dto.total ?? 0,
    done: dto.done ?? 0,
    results,
  };
}

// ── R390:`GET /ads/google/assets`(帳戶素材清單;docs/114 §9.2)──────────────

export function mapAdAccountAssetDto(dto: AdAccountAssetDto): AdAccountAsset {
  return {
    assetId: dto.asset_id,
    // 開放字串(BE 白名單現值 IMAGE / VIDEO;R296:不窮舉 wire 值域)
    kind: dto.kind,
    name: dto.name ?? null,
    imageUrl: dto.image_url ?? null,
    youtubeVideoId: dto.youtube_video_id ?? null,
    policyApprovalStatus: dto.policy_approval_status ?? null,
    hasServed: dto.has_served ?? null,
    importedItemId: dto.imported_item_id ?? null,
    // `served=never` 模式四欄恆 null(enrichment 是 BQ 面的)—— 缺欄照 null,不補
    assetGroupKey: dto.asset_group_key ?? null,
    assetGroupName: dto.asset_group_name ?? null,
    durationMillis: dto.duration_millis ?? null,
    lastSeenAt: dto.last_seen_at ?? null,
  };
}

export function mapAdAccountAssetsResponseDto(
  dto: AdAccountAssetsResponseDto,
): AdAccountAssetsResult {
  const assets = (dto.assets ?? []).map(mapAdAccountAssetDto);
  return {
    customerId: dto.customer_id,
    servedFilter: dto.served_filter ?? '',
    // `total` 在 never 模式 = 本次回傳數(D-6);缺欄退列數,不假裝知道更多
    total: dto.total ?? assets.length,
    assets,
  };
}

// ===== T9(docs/51)— AEI 疲乏聚合 =====

export function mapGroupAeiReportDto(dto: GroupAeiReportDto): GroupAeiReport {
  return {
    windowFrom: dto.window_from,
    windowTo: dto.window_to,
    assetCount: dto.asset_count ?? 0,
    assets: (dto.assets ?? []).map((a) => ({
      // 兩個素材鍵**各自映射,不互相 fallback** —— 哪一邊有值由請求的 `asset_source`
      // 決定,混用會把 closed-loop id 與 Google 原生 id 塞進同一個對照表(docs/89 §2)。
      ourAssetId: a.our_asset_id ?? null,
      assetId: a.asset_id ?? null,
      score: a.score,
      frequencyScore: a.frequency_score,
      ctrScore: a.ctr_score,
      vtrScore: a.vtr_score,
      vcrScore: a.vcr_score,
      cpcScore: a.cpc_score,
      missing: a.missing ?? [],
      weeksEvaluated: a.weeks_evaluated,
      weekOverWeekDelta: a.week_over_week_delta,
      isWorsening: a.is_worsening,
      // ADR-047(2026-09-14)兩個週欄:舊落表列 / 舊 BE 缺席 → null(「不知道」不是「第 0 週」)
      baselineWeekStart: a.baseline_week_start ?? null,
      evalWeekStart: a.eval_week_start ?? null,
    })),
    distribution: (dto.distribution ?? []).map((b) => ({
      label: b.label,
      rangeMin: b.range_min,
      rangeMax: b.range_max,
      count: b.count,
    })),
    vtrVcr: {
      avgVtr: dto.vtr_vcr?.avg_vtr ?? null,
      avgVcr: dto.vtr_vcr?.avg_vcr ?? null,
      sampleSize: dto.vtr_vcr?.sample_size ?? 0,
    },
    weeklyTrend: (dto.weekly_trend ?? []).map((w) => ({
      isoYear: w.iso_year,
      isoWeek: w.iso_week,
      weekStart: w.week_start,
      avgAei: w.avg_aei,
      assetCount: w.asset_count,
    })),
    dataAsOf: dto.data_as_of ?? null,
    // 截斷旗標(finding B):舊 BE 缺欄 → false(= 沒截斷),與欄位存在前的行為一致。
    hasMore: dto.has_more ?? false,
    computationBasis: dto.computation_basis ?? null,
  };
}

// ===== T7(docs/52)— 推素材上 Google Ads 素材庫 =====

export function createAssetImportsRequestToDto(
  req: CreateAssetImportsRequest,
): PostImportsRequestDto {
  return {
    customer_id: req.customerId,
    items: req.items.map((i) => ({ lib_id: i.libId, item_id: i.itemId })),
    // R407 — 只有 `true` 才上 wire:舊 BE 是 `extra="forbid"`,連 `force: false` 都是整批 422;
    // 一般上傳的 body 因此與加這欄之前逐字相同。
    ...(req.force === true ? { force: true as const } : {}),
  };
}

export function mapPostImportsResponseDto(
  dto: PostImportsResponseDto,
): CreateAssetImportsResponse {
  return {
    imports: (dto.imports ?? []).map((o) => ({
      libId: o.lib_id,
      itemId: o.item_id,
      assetKind: o.asset_kind,
      status: o.status,
      uploadId: o.upload_id,
      gaResourceName: o.ga_resource_name,
      alreadyExisted: o.already_existed ?? false,
      errorClass: o.error_class,
    })),
  };
}

function mapGaUploadDto(dto: GaUploadDto): GaUpload {
  return {
    uploadId: dto.upload_id,
    libId: dto.lib_id,
    itemId: dto.item_id,
    platform: dto.platform,
    customerId: dto.customer_id,
    assetKind: dto.asset_kind,
    status: dto.status,
    gaResourceName: dto.ga_resource_name,
    youtubeVideoId: dto.youtube_video_id,
    errorClass: dto.error_class,
    progressHint: dto.progress_hint,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  };
}

export function mapGaUploadEntityDto(dto: GaUploadDto): GaUpload {
  return mapGaUploadDto(dto);
}

// ===== M5(BE docs/118)— Meta 素材上傳 =====

/**
 * `POST /ads/meta/assets/uploads` 的 per-item 結果 snake → camel。
 *
 * ⚠️ **`already_existed` 缺欄一律當 `false`**:它是「沒有重打 Meta」的宣告,
 * 補成 `true` 等於替 BE 說「那顆素材已經在客戶帳戶裡了」—— 使用者會少傳一次而不自知。
 */
export function mapMetaAssetUploadOutcomeDto(
  dto: MetaAssetUploadOutcomeDto,
): MetaAssetUploadOutcome {
  return {
    libId: dto.lib_id ?? null,
    itemId: dto.item_id ?? null,
    projectId: dto.project_id ?? null,
    jobId: dto.job_id ?? null,
    assetId: dto.asset_id ?? null,
    assetKind: dto.asset_kind ?? null,
    status: dto.status,
    uploadId: dto.upload_id ?? null,
    imageHash: dto.image_hash ?? null,
    videoId: dto.video_id ?? null,
    alreadyExisted: dto.already_existed === true,
    errorClass: dto.error_class ?? null,
  };
}

export function mapCreateMetaAssetUploadsResponseDto(
  dto: CreateMetaAssetUploadsResponseDto,
): CreateMetaAssetUploadsResponse {
  return {
    platform: dto.platform,
    uploads: (dto.uploads ?? []).map(mapMetaAssetUploadOutcomeDto),
  };
}

export function mapMetaAssetUploadDto(dto: MetaAssetUploadDto): MetaAssetUpload {
  return {
    uploadId: dto.upload_id,
    platform: dto.platform,
    libId: dto.lib_id ?? null,
    itemId: dto.item_id ?? null,
    projectId: dto.project_id ?? null,
    jobId: dto.job_id ?? null,
    assetId: dto.asset_id ?? null,
    customerId: dto.customer_id,
    assetKind: dto.asset_kind,
    status: dto.status,
    imageHash: dto.image_hash ?? null,
    videoId: dto.video_id ?? null,
    errorClass: dto.error_class ?? null,
    progressHint: dto.progress_hint ?? null,
    createdAt: dto.created_at ?? null,
    updatedAt: dto.updated_at ?? null,
  };
}

// ── R325 — 跨來源素材分群(docs/117)────────────────────────────────────────

/**
 * 分群查詢 FE → wire。
 *
 * ⚠️ **白名單組裝**:BE request 是 `extra="forbid"`,多送一個欄(或 camelCase 鍵)
 * = **整包 422**,而這一頁的 422 症狀是「整張表讀取失敗」。所以這裡逐欄點名,
 * 絕不 spread 呼叫端的物件;新欄要送,先來這裡登記。
 *
 * ⚠️ `includeMetrics` 必配 `dateRange`(BE model_validator;成效表
 * `require_partition_filter=TRUE`,沒有窗 BQ 直接拒查)—— 這裡鏡射成 throw,
 * 讓寫錯的呼叫端在測試就死,而不是上線後每次查詢 422。
 */
export function assetGroupsQueryToDto(q: AssetGroupsQuery): AssetGroupsQueryRequestDto {
  if (q.includeMetrics && !q.dateRange) {
    throw new Error('assetGroupsQueryToDto: includeMetrics=true requires dateRange (BE 422)');
  }
  const out: AssetGroupsQueryRequestDto = {};
  if (q.similarityLevel) out.similarity_level = q.similarityLevel;
  if (q.sources?.length) out.sources = [...q.sources];
  // date_range 只是成效窗:不開成效時 BE 會忽略它 → 乾脆不送,wire 上少一個欄
  if (q.includeMetrics && q.dateRange) out.date_range = q.dateRange;
  if (q.customerIds?.length) out.filters = { customer_ids: [...q.customerIds] };
  if (q.minMembers !== undefined) out.min_members = q.minMembers;
  if (q.onlyCrossSource) out.only_cross_source = true;
  if (q.includeMetrics) out.include_metrics = true;
  if (q.memberLimit !== undefined) out.member_limit = q.memberLimit;
  if (q.limit !== undefined) out.limit = q.limit;
  if (q.cursor) out.cursor = q.cursor;
  return out;
}

function mapAssetGroupThumbDto(dto: AssetGroupThumbDto | null | undefined): AssetGroupThumb {
  return {
    source: dto?.source ?? null,
    url: dto?.url ?? null,
    gcsUri: dto?.gcs_uri ?? null,
  };
}

function mapAssetGroupMemberDto(dto: AssetGroupMemberDto): AssetGroupMember {
  return {
    source: dto.source ?? '',
    platform: dto.platform ?? null,
    customerId: dto.customer_id ?? null,
    assetId: dto.asset_id ?? '',
    kind: dto.kind ?? 'OTHER',
    name: dto.name ?? null,
    thumb: mapAssetGroupThumbDto(dto.thumb),
    libraryItemId: dto.library_item_id ?? null,
    sourceLabel: dto.source_label ?? null,
    durationMillis: dto.duration_millis ?? null,
    // ⚠️ Google 成員的 first_seen BE 誠實留 null(clean 表沒有首見時間)——
    // 這裡照樣傳 null,呈現端顯「—」;補一個 ingest 時間冒充首見就是 R263 那族。
    firstSeen: dto.first_seen ?? null,
    lastSeen: dto.last_seen ?? null,
  };
}

/**
 * `metrics` 的 null 判準:**dto 給 null 就是 null**(docs/117 §3.1:null 有四種合法
 * 情形,都不是錯誤)。⚠️ 不要把 null 補成一包 0 —— 0 是「有量測、值為零」,null 是
 * 「沒有這個量測」(R263 / R295 那族);BE 也保證不會回「數字全 null 卻聲稱有貢獻」
 * 的物件。
 */
function mapAssetGroupMetricsDto(dto: AssetGroupMetricsDto | null | undefined): AssetGroupMetrics | null {
  if (!dto) return null;
  return {
    impressions: dto.impressions ?? null,
    clicks: dto.clicks ?? null,
    costAmount: dto.cost_amount ?? null,
    conversions: dto.conversions ?? null,
    videoViews: dto.video_views ?? null,
    currencyCode: dto.currency_code ?? null,
    currencyMixed: dto.currency_mixed ?? false,
    platforms: dto.platforms ?? [],
  };
}

function mapAssetGroupRowDto(dto: AssetGroupRowDto): AssetGroupRow {
  return {
    similarKey: dto.similar_key ?? '',
    canonical: dto.canonical ?? null,
    memberCount: dto.member_count ?? 0,
    sourceCounts: {
      google: dto.source_counts?.google ?? 0,
      meta: dto.source_counts?.meta ?? 0,
      platform: dto.source_counts?.platform ?? 0,
    },
    kinds: dto.kinds ?? [],
    displayName: dto.display_name ?? null,
    thumb: mapAssetGroupThumbDto(dto.thumb),
    firstSeen: dto.first_seen ?? null,
    lastSeen: dto.last_seen ?? null,
    members: (dto.members ?? []).map(mapAssetGroupMemberDto),
    metrics: mapAssetGroupMetricsDto(dto.metrics),
  };
}

export function mapAssetGroupsResponseDto(dto: AssetGroupsResponseDto): AssetGroupsResult {
  return {
    rows: (dto.rows ?? []).map(mapAssetGroupRowDto),
    similarityLevel: dto.similarity_level ?? 'strict',
    // ⚠️ 缺欄退空陣列而不是三個全給:sources 的語意是「實際涵蓋」,補滿等於替 BE
    // 宣告它查了沒查的來源(涵蓋提示會因此消失,零徵兆)。
    sources: dto.sources ?? [],
    dataAsOf: dto.data_as_of ?? null,
    dataAsOfByPlatform: dto.data_as_of_by_platform ?? {},
    nextCursor: dto.next_cursor ?? null,
    totalRows: dto.total_rows ?? null,
    aggregationCaveat: dto.aggregation_caveat ?? 'asset_grain_non_additive',
  };
}
